/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { filter, noop } from 'lodash'
import React, { Component, Fragment } from 'react'
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormLabel,
  EuiIconTip,
  EuiButtonEmpty,
  EuiLoadingSpinner,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import type { StackVersionConfig } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, StackDeployment, Region, VersionNumber } from '@modules/ui-types'
import { withErrorBoundary } from '@modules/cui/Boundary'
import { CuiHelpTipIcon } from '@modules/cui/HelpTipIcon'
import { CuiAlert } from '@modules/cui/Alert'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { hasMismatchingVersions } from '@/lib/stackDeployments/upgradesToSliders'
import { isSystemOwned } from '@/lib/stackDeployments/selectors/metadata'
import { getRegionId, getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { satisfies } from '@/lib/semver'

import DeploymentLockGate from '../../../DeploymentLockGate'
import DocLink from '../../../DocLink'

import PartialUpgradeTooltip from './PartialUpgradeTooltip'
import DeploymentVersionUpgradeModal from './DeploymentVersionUpgradeModal'

import type { WrappedComponentProps } from 'react-intl'

import './upgradableDeploymentVersion.scss'

const messages = defineMessages({
  ariaLabel: {
    id: `upgradable-deployment-version.aria`,
    defaultMessage: `More information`,
  },
})

export type StateProps = {
  availableVersions?: string[]
  esVersionRequest: AsyncRequestState
  lowestVersion?: VersionNumber | null
  region?: Region
  versionStack?: StackVersionConfig
  showModalOnMount: boolean
}

export type DispatchProps = {
  fetchVersionStack: (version: string, regionId: string) => Promise<unknown>
  fetchVersionStacks: (regionId: string) => void
  fetchRegion: (regionId: string) => void
}

export type ConsumerProps = {
  deployment: StackDeployment
}

export type Props = WrappedComponentProps & StateProps & DispatchProps & ConsumerProps

type State = {
  showUpgradeModal: boolean
}

class UpgradableDeploymentVersion extends Component<Props, State> {
  state: State = {
    showUpgradeModal: false,
  }

  async componentDidMount() {
    const { showModalOnMount, deployment, fetchVersionStack, lowestVersion } = this.props

    // If a deployment is partially upgraded, we pick the lowest
    // found version found in each resource to upgrade from.
    const regionId = getRegionId({ deployment })

    if (!lowestVersion) {
      return
    }

    fetchVersionStack(lowestVersion, regionId)
      .then(({ payload }) => {
        if (showModalOnMount) {
          if (payload?.upgradable_to?.length) {
            this.showUpgrade()
          }
        }
      })
      .catch(noop)
  }

  render() {
    const { deployment, versionStack } = this.props
    const version = getVersion({ deployment })

    if (version == null) {
      return null
    }

    const isVersionDeleted = versionStack != null ? versionStack.deleted : false

    return (
      <div>
        <EuiFormLabel>
          <EuiFlexGroup gutterSize='none' alignItems='center'>
            <EuiFlexItem grow={false}>
              <FormattedMessage
                id='upgradable-deployment-version.deployment-version'
                defaultMessage='Deployment version'
              />
            </EuiFlexItem>

            {isVersionDeleted && (
              <EuiFlexItem grow={false}>
                <span
                  data-test-id='upgradable-deployment-version-deleted'
                  style={{ verticalAlign: `text-bottom`, marginLeft: 4 }}
                >
                  <EuiIconTip
                    size='m'
                    aria-label='Version deleted'
                    content={
                      <FormattedMessage
                        id='upgradable-deployment-version.version-deleted'
                        defaultMessage='This Elastic Stack version has been deleted. Please upgrade to a new version.'
                      />
                    }
                    type='alert'
                    color='warning'
                  />
                </span>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFormLabel>
        <EuiSpacer size='s' />

        {this.renderContent()}
      </div>
    )
  }

  renderContent() {
    const { deployment, availableVersions, esVersionRequest } = this.props
    const version = getVersion({ deployment })!

    if (esVersionRequest.error) {
      return (
        <Fragment>
          <EuiSpacer size='s' />
          <CuiAlert type='error' data-test-id='es-version-request-error'>
            {esVersionRequest.error}
          </CuiAlert>
        </Fragment>
      )
    }

    if (availableVersions == null) {
      return (
        <Fragment>
          <EuiSpacer size='s' />
          <EuiLoadingSpinner />
        </Fragment>
      )
    }

    const upgradeAvailable = this.getAvailableVersions().length > 0

    return (
      <Fragment>
        <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
          <EuiFlexItem grow={false} data-test-id='deployment-version'>
            <EuiText size='s'>
              {
                // tslint:disable-next-line:jsx-use-translation-function
                `v${version}`
              }
            </EuiText>
          </EuiFlexItem>

          <DeploymentLockGate>{this.renderStartUpgradeButton(upgradeAvailable)}</DeploymentLockGate>
        </EuiFlexGroup>

        {this.renderUpgradeModal()}
      </Fragment>
    )
  }

  renderStartUpgradeButton(anySelectableVersions) {
    const {
      deployment,
      intl: { formatMessage },
    } = this.props
    const { showUpgradeModal } = this.state

    const isHidden = showUpgradeModal || !anySelectableVersions
    const isUpgradeIncomplete = hasMismatchingVersions({ deployment })

    return (
      <EuiFlexItem
        grow={false}
        className={isHidden ? `upgradableDeploymentVersion-hidden` : undefined}
        aria-hidden={isHidden}
      >
        <div className='docLink'>
          {isUpgradeIncomplete ? (
            <PartialUpgradeTooltip deployment={deployment} />
          ) : (
            <CuiHelpTipIcon aria-label={formatMessage(messages.ariaLabel)} color='primary'>
              <span data-test-id='upgrade-available'>
                <FormattedMessage
                  id='upgradable-deployment-version.upgrade-available'
                  defaultMessage='There is a newer version available. {learnMore}'
                  values={{
                    learnMore: (
                      <DocLink link='upgradingDocLink'>
                        <FormattedMessage
                          id='upgradable-deployment-version.upgrade-available-link'
                          defaultMessage='Learn more'
                        />
                      </DocLink>
                    ),
                  }}
                />
              </span>
            </CuiHelpTipIcon>
          )}

          <PermissionsGate
            permissions={[
              {
                type: 'deployment',
                action: 'upgrade',
                id: deployment.id,
              },
            ]}
          >
            {({ hasPermissions }) => (
              <EuiButtonEmpty
                flush='both'
                style={{ paddingLeft: `.3rem` }}
                disabled={!hasPermissions}
                onClick={() => this.showUpgrade()}
                data-test-id='upgradable-deployment-version-show-available'
              >
                <FormattedMessage
                  id='upgradable-deployment-version.upgrade-link'
                  defaultMessage='Upgrade'
                />
              </EuiButtonEmpty>
            )}
          </PermissionsGate>
        </div>
      </EuiFlexItem>
    )
  }

  renderUpgradeModal() {
    const { deployment, region } = this.props
    const { showUpgradeModal } = this.state

    if (!showUpgradeModal) {
      return null
    }

    return (
      <DeploymentVersionUpgradeModal
        deployment={deployment}
        isLoadingRegion={!region}
        onCancel={this.cancelShowUpgrade}
      />
    )
  }

  showUpgrade() {
    const { deployment, fetchRegion, fetchVersionStacks } = this.props

    const regionId = getRegionId({ deployment })!

    fetchRegion(regionId)
    fetchVersionStacks(regionId)

    this.setState({ showUpgradeModal: true })
  }

  cancelShowUpgrade = () => {
    this.setState({ showUpgradeModal: false })
  }

  getAvailableVersions = () => {
    const { deployment, availableVersions } = this.props

    if (!availableVersions) {
      return []
    }

    // temporary fix, still waiting for the API to provide upgrade available for system owned deployment
    // see https://github.com/elastic/cloud/issues/31782
    return isSystemOwned({ deployment })
      ? filter(availableVersions, (version) => satisfies(version, `<7`))
      : availableVersions
  }
}

export default withErrorBoundary(injectIntl(UpgradableDeploymentVersion))
